/**
 * Customer sign-in (identity contract): the email gets a 6-digit sign-in code (POST /email-codes), typed back here
 * (POST /email-codes/verify); then the second step - authenticator app code or passkey, whichever the account has - or a
 * first-time choice between the two for roles that require it. Older emails still carry a `#token=` link (sign-up
 * verification, audit hand-off): it is consumed here (fragment removed with history.replaceState) and continues the same
 * way (a new self-serve owner lands here from the verification email). The email step and sign-up use Cloudflare Turnstile
 * when enabled (invisible unless Cloudflare asks; lib/turnstile.ts); the 2FA field is a labelled numeric
 * input with autocomplete="one-time-code" and errors announced via role="alert", so the flow is completable
 * with a screen reader (QM-NX-016 security scenario). Returns to ?next= after sign-in (QM-D19-002).
 * Rendered standalone (AuthLayout), outside the portal shell. Also served at /sign-in and /sign-in/link.
 * Passkeys (flag identity.passkeys; hidden while the API answers 404): "Sign in with a passkey" plus conditional
 * mediation on the email field, a passkey option at the 2FA step, and passkey setup as an alternative to TOTP.
 * Recovery: the 2FA step accepts a recovery code (POST /2fa/recover -> forced re-enrolment); a first passkey on an
 * account without codes returns a set, shown once like the TOTP ones.
 * Google and Microsoft (flags identity.{google,microsoft}_signin.*; each hidden while its /status says no): the callback
 * lands here with ?error= (&via=microsoft) when refused, or ?mfa= when a second factor is still owed (lib/google-signin.tsx).
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Button, ErrorState, Field, Heading, Panel, Text, TextInput } from '@qm/ui';
import { surfaceForEmailDomain } from '@qm/contracts/identity';
import { adminOriginFor } from '@qm/tenancy/surface';
import { loadSession, useApp } from '../../../lib/app-context';
import { BffError, bff, bffJson } from '../../../lib/bff';
import { botCheckHeaders } from '../../../lib/turnstile';
import { isInstalledApp, keepSignedInWanted, setKeepSignedInWanted } from '../../../lib/keep-signed-in';
import { autofillSupported, passkeyErrorKey, passkeySecondFactor, passkeySignIn, passkeysSupported, registerPasskey, signInOptions } from '../../../lib/passkeys';
import { authDestination, rememberNext, safeNext, tokenFromHash } from '../../../lib/deeplink';
import {
  GOOGLE_START_URL, GoogleButton, MICROSOFT_START_URL, MicrosoftButton, googleErrorSuffix, googleMfaResume, socialErrorProvider, useGoogleSignin,
  useMicrosoftSignin,
} from '../../../lib/google-signin';

import { AuthenticatorSetup, SecurityChoice } from './SecuritySetup';
import { PhoneVerification } from '../setup/PhoneVerification';
import type { PhoneStatus } from '@qm/contracts/customer-setup';
export { tokenFromHash };

type Step = 'email' | 'code' | 'verifying' | 'totp' | 'enrol' | 'recovery' | 'phone';

/** consume -> which step comes next ('mfa' is the API value; older values kept for compatibility). */
/** What POST /email-codes/verify and /magic-links/consume answer. */
interface FirstFactorResult { next?: string; mfaMethods?: string[] }

export function stepAfterConsume(next: string | undefined): 'totp' | 'enrol' | 'app' {
  if (next === 'mfa' || next === 'mfa_verify' || next === 'totp') return 'totp';
  if (next === 'enrol_mfa') return 'enrol';
  return 'app';
}

const numeric = (v: string) => v.replace(/[٠-٩۰-۹]/g, c => String(c.charCodeAt(0) - (c <= '٩' ? 0x660 : 0x6f0))).replace(/\D/g, '');

/** Which second-factor controls the 2FA step shows (consume returns mfaMethods; absent = TOTP only). */
export function secondFactorControls(methods: string[] | undefined): { code: boolean; passkey: boolean } {
  const m = methods?.length ? methods : ['totp'];
  return { code: m.includes('totp'), passkey: m.includes('passkey') };
}

export function SignInPage() {
  const { t, locale } = useApp();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const nextQuery = params.get('next') ? `?next=${encodeURIComponent(next)}` : '';
  const googleResume = googleMfaResume(params);
  const googleOn = useGoogleSignin();
  const microsoftOn = useMicrosoftSignin();
  const googleError = googleErrorSuffix(params.get('error'));
  const errorProvider = socialErrorProvider(params);
  const [step, setStep] = useState<Step>(() => (!googleResume ? 'email' : stepAfterConsume(googleResume.next) === 'totp' ? 'totp' : 'verifying'));
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [resent, setResent] = useState(false);
  const [sending, setSending] = useState(false);
  const codeForm = useRef<HTMLFormElement>(null);
  const [factor, setFactor] = useState<{ factorId: string; secret: string; otpauthUri: string } | null>(null);
  const [recovery, setRecovery] = useState<string[]>([]);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoverySaved, setRecoverySaved] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const consumed = useRef(false);
  const [error, setError] = useState<string | null>(() => (googleError ? t(`shell.signin.${errorProvider}Error.${googleError}`) : null));
  // Owner request 27 Sep 2026: a @quixohub.com / @quixomarketing.com email typed here belongs on the staff
  // sign-in. Domain-only, client-side, no account lookup - see @qm/contracts/identity surfaceForEmailDomain.
  // G14: the owner tests the customer flow with a staff address, so "Continue as a customer" keeps the email code here
  const [asCustomer, setAsCustomer] = useState(false);
  const isStaffEmail = surfaceForEmailDomain(email) === 'admin' && !asCustomer;
  const staffSigninUrl = typeof window === 'undefined' ? '/signin' : `${adminOriginFor(window.location.host, window.location.protocol)}/signin`;
  const staffMicrosoftUrl = typeof window === 'undefined' ? '/signin' : `${adminOriginFor(window.location.host, window.location.protocol)}/bff/v1/identity/entra/start`;
  const [passkeyOn, setPasskeyOn] = useState(false);
  const installedApp = isInstalledApp();
  const [keepSignedIn, setKeepSignedIn] = useState(() => keepSignedInWanted());
  const [methods, setMethods] = useState<string[] | undefined>(googleResume?.methods);
  const controls = secondFactorControls(methods);
  const finish = () => window.location.assign(authDestination(next));

  const startEnrol = async (phoneChecked = false) => {
    setFactor(null); setCode('');
    if (passkeysSupported()) void bff('/v1/identity/passkeys').then(() => setPasskeyOn(true), () => undefined);
    if (!phoneChecked) {
      try { const p = await bff<PhoneStatus>('/v1/identity/phone/status'); if (p.required && !p.verified) { setStep('phone'); return; } }
      catch { setError(t('shell.error.action')); setStep('email'); return; }
    }
    setStep('enrol');
  };
  const beginAuthenticator = async () => {
    if (enrolling) return;
    setEnrolling(true); setError(null);
    try { setFactor(await bffJson<{factorId:string;secret:string;otpauthUri:string}>('/v1/identity/2fa/totp/enrol','POST',{})); }
    catch { setError(t('shell.error.action')); }
    finally { setEnrolling(false); }
  };
  // Email code or link accepted: on to the second step the account needs (or straight in).
  const afterFirstFactor = (r: FirstFactorResult) => {
    setMethods(r.mfaMethods);
    const s = stepAfterConsume(r.next);
    if (s === 'totp') setStep('totp');
    else if (s === 'enrol') void startEnrol();
    else finish();
  };
  useEffect(() => { if (params.get('next')) rememberNext(params.get('next')); }, [params]);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (consumed.current) return;
    const token = tokenFromHash(window.location.hash);
    if (token) {
      if (consumed.current) return;
      consumed.current = true;
      history.replaceState(null, '', window.location.pathname + window.location.search);
      setStep('verifying');
      bffJson<FirstFactorResult>('/v1/identity/magic-links/consume', 'POST', { token }).then(
        afterFirstFactor,
        () => { setError(t('shell.signin.linkInvalid')); setStep('email'); },
      );
      return;
    }
    // Back from Google with a second factor still owed: straight to that step (GET /me answers 401 until it is done).
    if (googleResume) { if (googleResume.next === 'enrol_mfa') void startEnrol(); return; }
    // Passkey sign-in: offered only when the API answers (flag on); autofill waits for a pick from the email field.
    let live = true;
    void signInOptions().then(async (options) => {
      if (!options || !live) return;
      setPasskeyOn(true);
      if (await autofillSupported()) passkeySignIn({ options, autofill: true }).then(finish, () => undefined);
    });
    // Already signed in (deep link re-auth): resume ?next= without asking for the email again.
    if (params.get('next')) loadSession().then((s) => { if (!live || !s) return; if (s.me.mfa.state === 'enrol_required') void startEnrol(); else window.location.assign(authDestination(next)); });
    return () => { live = false; };
    // runs once per page load: the token is single use
  }, []);

  // Always answered 202 (no account enumeration): the page moves to the code step whatever the address.
  const sendCode = async (el: HTMLElement) => {
    if (sending) return false;
    setSending(true); setError(null);
    try {
      await bff('/v1/identity/email-codes', { method: 'POST', headers: await botCheckHeaders(el, locale), body: JSON.stringify({ email: email.trim(), locale }) });
      return true;
    } catch (err) { setError(err instanceof BffError && err.status === 403 ? t('shell.error.botCheck') : t('shell.error.action')); return false; }
    finally { setSending(false); }
  };
  const request = async (e: FormEvent) => {
    e.preventDefault(); setError(null);
    // Staff accounts have no email code: a @quixohub.com / @quixomarketing.com email goes to Microsoft sign-in on the admin site.
    if (isStaffEmail) { window.location.assign(staffMicrosoftUrl); return; }
    if (await sendCode(e.currentTarget as HTMLElement)) { setEmailCode(''); setResent(false); setStep('code'); }
  };
  const resend = async () => {
    if (codeForm.current && await sendCode(codeForm.current)) { setEmailCode(''); setResent(true); }
  };
  const verifyEmailCode = async (e: FormEvent) => {
    e.preventDefault(); setError(null); setResent(false);
    try { afterFirstFactor(await bffJson<FirstFactorResult>('/v1/identity/email-codes/verify', 'POST', { email: email.trim(), code: emailCode })); }
    catch { setError(t('shell.signin.emailCodeInvalid')); }
  };
  const changeEmail = () => { setStep('email'); setEmailCode(''); setError(null); setResent(false); };
  const verify = async (e: FormEvent) => {
    e.preventDefault(); setError(null);
    try { await bffJson('/v1/identity/2fa/verify', 'POST', { code }); window.location.assign(authDestination(next)); } catch { setError(t('shell.signin.codeInvalid')); }
  };
  const withPasskey = (run: () => Promise<unknown>, after: () => void = finish) => async () => {
    setError(null);
    try { await run(); after(); } catch (err) { setError(t(passkeyErrorKey(err))); }
  };
  const signInWithPasskey = withPasskey(() => passkeySignIn());
  const verifyWithPasskey = withPasskey(() => passkeySecondFactor());
  // A passkey-only account receives recovery codes once with its first passkey: show them before continuing.
  const enrolPasskey = async () => {
    if (enrolling) return;
    setEnrolling(true); setError(null);
    try {
      const r = await registerPasskey(t('shell.passkeys.defaultName'));
      if (r.recoveryCodes?.length) { setRecovery(r.recoveryCodes); setFactor(null); setStep('recovery'); } else finish();
    } catch (err) { setError(t(passkeyErrorKey(err))); } finally { setEnrolling(false); }
  };
  // Lost device: a recovery code opens the session once, disables every factor and forces re-enrolment.
  const recover = async (e: FormEvent) => {
    e.preventDefault(); setError(null);
    try { await bffJson('/v1/identity/2fa/recover', 'POST', { code: recoveryCode.trim() }); setRecoveryCode(''); void startEnrol(); } catch { setError(t('shell.signin.codeInvalid')); }
  };
  const confirm = async (e: FormEvent) => {
    e.preventDefault(); setError(null);
    if (!factor) return;
    try {
      const r = await bffJson<{ recoveryCodes: string[] }>('/v1/identity/2fa/totp/confirm', 'POST', { factorId: factor.factorId, code });
      setRecovery(r.recoveryCodes); setFactor(null); setStep('recovery');
    } catch { setError(t('shell.signin.codeInvalid')); }
  };

  const codeField = (
    <Field label={t('shell.signin.code')} hint={step === 'totp' ? t('shell.signin.codeHint') : undefined} error={error}>
      {({ id, describedBy, invalid }) => <TextInput id={id} inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={code} aria-describedby={describedBy} aria-invalid={invalid || undefined} onChange={(e) => setCode(numeric(e.target.value))} />}
    </Field>
  );

  return (
    <section className="qm-page" aria-labelledby="page-title" data-testid="signin-page">
      <Panel>
        <Heading level={1} id="page-title">{step === 'enrol' ? t('shell.signin.enrolTitle') : step === 'recovery' ? t('shell.signin.recoveryTitle') : t('shell.signin.title')}</Heading>
        {step === 'email' ? (
          <>
            <Text>{t('shell.signin.subtitle')}</Text>
            <form onSubmit={request} noValidate>
              <Field label={t('shell.signin.email')} error={error}>
                {({ id, describedBy, invalid }) => <TextInput id={id} type="email" autoComplete={passkeyOn ? 'username webauthn' : 'email'} required value={email} aria-describedby={describedBy} aria-invalid={invalid || undefined} onChange={(e) => setEmail(e.target.value)} />}
              </Field>
              {installedApp ? (
                <label className="qm-keep-signed-in" data-testid="keep-signed-in">
                  <input type="checkbox" checked={keepSignedIn} onChange={(e) => { setKeepSignedIn(e.target.checked); setKeepSignedInWanted(e.target.checked); }} />
                  {' '}{t('shell.signin.keepSignedIn')}
                </label>
              ) : null}
              <Button variant="primary" type="submit" disabled={sending} data-testid="send-code">{t('shell.signin.sendCode')}</Button>
            </form>
            {passkeyOn || googleOn || microsoftOn ? <Text tone="muted" className="qm-auth__or">{t('shell.signin.or')}</Text> : null}
            {googleOn ? <GoogleButton label={t('shell.signin.google')} onClick={() => window.location.assign(GOOGLE_START_URL)} testId="google-signin" /> : null}
            {microsoftOn ? <MicrosoftButton label={t('shell.signin.microsoft')} onClick={() => window.location.assign(MICROSOFT_START_URL)} testId="microsoft-signin" /> : null}
            {passkeyOn ? <Button type="button" onClick={() => void signInWithPasskey()} data-testid="passkey-signin">{t('shell.signin.passkey')}</Button> : null}
            <Text>{t('shell.signin.noAccount')} <Link to={`/signup${nextQuery}`} data-testid="to-signup">{t('shell.signin.toSignup')}</Link></Text>
            {isStaffEmail ? (
              <Text tone="muted" data-testid="staff-hint">{t('shell.signin.staffHint')} <a href={staffSigninUrl} data-testid="staff-signin-link">{t('shell.signin.staffLink')}</a>{' '}
                <button type="button" className="qm-link-button" data-testid="continue-as-customer" onClick={() => setAsCustomer(true)}>{t('shell.p1.signin.asCustomer')}</button>
              </Text>
            ) : asCustomer ? <Text tone="muted" role="status">{t('shell.p1.signin.asCustomerNote')}</Text> : null}
          </>
        ) : step === 'code' ? (
          <>
            <Text role="status" data-testid="code-sent">{t('shell.signin.codeSent').replace('{email}', email.trim())}</Text>
            <form ref={codeForm} onSubmit={verifyEmailCode} noValidate data-testid="email-code-form">
              <Field label={t('shell.signin.emailCode')} hint={t('shell.signin.emailCodeHint')} error={error}>
                {({ id, describedBy, invalid }) => <TextInput id={id} inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" required autoFocus dir="ltr" value={emailCode} aria-describedby={describedBy} aria-invalid={invalid || undefined} onChange={(e) => setEmailCode(numeric(e.target.value).slice(0, 6))} data-testid="email-code" />}
              </Field>
              <Button variant="primary" type="submit" disabled={emailCode.length !== 6}>{t('shell.signin.verify')}</Button>
            </form>
            {resent ? <Text tone="muted" role="status">{t('shell.signin.codeResent')}</Text> : null}
            <Button type="button" variant="secondary" disabled={sending} onClick={() => void resend()} data-testid="resend-code">{t('shell.signin.resendCode')}</Button>
            <button type="button" className="qm-link-button" onClick={changeEmail} data-testid="change-email">{t('shell.signin.changeEmail')}</button>
          </>
        ) : step === 'verifying' ? (
          <Text role="status">{t('common.loading')}</Text>
        ) : step === 'totp' ? (
          <>
            <Text>{controls.code && controls.passkey ? t('shell.signin.secondStepChoose') : controls.code ? t('shell.signin.secondStepCode') : t('shell.signin.passkeyPrompt')}</Text>
            {controls.code ? (
              <form onSubmit={verify} noValidate>
                {codeField}
                <Button variant="primary" type="submit">{t('shell.signin.verify')}</Button>
              </form>
            ) : null}
            {controls.passkey ? <Button variant={controls.code ? 'secondary' : 'primary'} type="button" onClick={() => void verifyWithPasskey()} data-testid="passkey-2fa">{t('shell.signin.passkeyVerify')}</Button> : null}
            {!controls.code && error ? <ErrorState message={error} /> : null}
            {showRecovery ? (
              <form onSubmit={recover} noValidate data-testid="recovery-form" id="recovery-form">
                <Field label={t('shell.signin.recoveryCode')}>
                  {({ id, describedBy }) => <TextInput id={id} autoComplete="off" spellCheck={false} dir="ltr" maxLength={32} required autoFocus value={recoveryCode} aria-describedby={describedBy} onChange={(e) => setRecoveryCode(e.target.value)} />}
                </Field>
                <Button type="submit" disabled={recoveryCode.trim().length < 8}>{t('shell.signin.recoveryUse')}</Button>
              </form>
            ) : (
              <Button type="button" variant="secondary" aria-expanded={false} aria-controls="recovery-form" onClick={() => setShowRecovery(true)} data-testid="recovery-toggle">{t('shell.signin.recoveryToggle')}</Button>
            )}
          </>
        ) : step === 'phone' ? (
          <PhoneVerification onVerified={() => void startEnrol(true)} />
        ) : step === 'enrol' ? (
          <>
            {!factor ? <SecurityChoice passkeyAvailable={passkeyOn} onAuthenticator={() => void beginAuthenticator()} onPasskey={() => void enrolPasskey()} busy={enrolling} /> : <>
              <AuthenticatorSetup factor={factor} />
              <form onSubmit={confirm} noValidate>{codeField}<Button variant="primary" type="submit">{t('shell.signin.enrolConfirm')}</Button></form>
              <Button type="button" onClick={() => { setFactor(null); setCode(''); setError(null); }}>{t('shell.p1.signin.otherMethod')}</Button>
            </>}
            {error ? <ErrorState message={error} /> : null}
          </>        ) : (
          <>
            <Text role="status">{t('shell.signin.recoveryIntro')}</Text>
            <ul className="qm-auth__codes" dir="ltr">{recovery.map((rc) => <li key={rc}>{rc}</li>)}</ul>
            <label className="qm-setup__check"><input type="checkbox" checked={recoverySaved} onChange={e => setRecoverySaved(e.target.checked)} />{t('shell.p1.signin.recoverySaved')}</label>
            <Button variant="primary" type="button" disabled={!recoverySaved} onClick={() => window.location.assign(authDestination(next))}>{t('shell.signin.continue')}</Button>
          </>
        )}
        {error && step !== 'email' && step !== 'code' && step !== 'totp' && step !== 'enrol' ? <ErrorState message={error} /> : null}
      </Panel>
    </section>
  );
}
